import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, X, Eye, Clock, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { formatPrice, catMeta, regionLabel, imgUrl, PLACEHOLDER_IMAGE } from "../lib/format";

export default function Admin() {
  const { lang } = useLang();
  const [filter, setFilter] = useState("awaiting_review");
  const [listings, setListings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/listings", { params: { status: filter } }),
      api.get("/admin/payments"),
    ]).then(([l, p]) => { setListings(l.data); setPayments(p.data); })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { await api.post(`/admin/listings/${id}/approve`); load(); };
  const reject = async (id) => { await api.post(`/admin/listings/${id}/reject`); load(); };

  const paymentFor = (lid) => payments.find((p) => p.listing_id === lid);

  const filters = [
    { key: "awaiting_review", en: "Awaiting Review", fr: "En révision" },
    { key: "active", en: "Published", fr: "Publiées" },
    { key: "pending", en: "Unpaid", fr: "Non payées" },
    { key: "rejected", en: "Rejected", fr: "Rejetées" },
    { key: "all", en: "All", fr: "Toutes" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-16">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="text-gold" />
        <h1 className="font-display text-3xl md:text-4xl text-white">{lang === "en" ? "Admin Panel" : "Panneau Admin"}</h1>
      </div>
      <p className="text-white/40 text-sm mb-8">{lang === "en" ? "Confirm MoMo payments and publish listings." : "Confirmez les paiements MoMo et publiez les annonces."}</p>

      <div className="flex gap-2 mb-8 flex-wrap">
        {filters.map((f) => (
          <button key={f.key} data-testid={`admin-filter-${f.key}`} onClick={() => setFilter(f.key)} className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${filter === f.key ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-white/40"}`}>
            {f[lang]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-28 shimmer" />)}</div>
      ) : listings.length === 0 ? (
        <div className="text-center py-24 border border-white/10 bg-[#121212] text-white/50">{lang === "en" ? "Nothing here." : "Rien ici."}</div>
      ) : (
        <div className="space-y-4">
          {listings.map((l) => {
            const meta = catMeta(l.category);
            const pay = paymentFor(l.id);
            const img = imgUrl(l.images?.[0]) || PLACEHOLDER_IMAGE;
            return (
              <div key={l.id} className="bg-[#121212] border border-white/10 p-4 flex flex-col lg:flex-row gap-4 lg:items-center" data-testid={`admin-listing-${l.id}`}>
                <img src={img} alt="" className="w-full lg:w-28 h-32 lg:h-20 object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{l.title}</p>
                  <p className="text-white/40 text-sm">{l.city}, {regionLabel(l.region, lang)} · {meta[lang]}</p>
                  <p className="text-gold font-display mt-1">{formatPrice(l.price)}</p>
                </div>
                <div className="text-sm shrink-0 lg:w-56">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{lang === "en" ? "Payment claim" : "Paiement déclaré"}</p>
                  {pay ? (
                    <div className="text-white/70">
                      <p>{pay.phone}</p>
                      <p className="text-white/40 text-xs">Ref: {pay.reference || l.id}</p>
                      <p className="text-gold text-xs">{formatPrice(pay.amount)} · {pay.status}</p>
                      {pay.confirmation_code && (
                        <p className="mt-1" data-testid={`admin-code-${l.id}`}>
                          <span className="text-white/40 text-xs">Code: </span>
                          <span className="font-display text-gold tracking-[0.2em] text-base">{pay.confirmation_code}</span>
                        </p>
                      )}
                    </div>
                  ) : <p className="text-white/30">{lang === "en" ? "No claim yet" : "Aucun"}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  {l.status !== "active" && (
                    <button data-testid={`approve-${l.id}`} onClick={() => approve(l.id)} className="flex items-center gap-2 bg-green-500/15 border border-green-500/40 text-green-300 px-4 py-2.5 text-sm hover:bg-green-500/25 transition-colors">
                      <Check size={16} /> {lang === "en" ? "Publish" : "Publier"}
                    </button>
                  )}
                  {l.status === "active" && <Link to={`/listing/${l.id}`} className="flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2.5 text-sm hover:border-gold transition-colors"><Eye size={16} /> {lang === "en" ? "View" : "Voir"}</Link>}
                  {l.status !== "rejected" && (
                    <button data-testid={`reject-${l.id}`} onClick={() => reject(l.id)} className="flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2.5 text-sm hover:border-red-500 hover:text-red-300 transition-colors">
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
